import { EmailLinkCallback } from '../EmailLinkCallback';
import React, { FormEvent, useEffect, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, AlertIcon, Box, Button, FormControl, FormLabel, Heading, Input, Modal, ModalBody, ModalContent, ModalOverlay, Text, Tooltip, VStack } from '@chakra-ui/react';
import { EmailLoginLinkResult, MutationAuthenticateEmailLoginCodeArgs, Mutation, MutationRequestEmailLoginLinkArgs, MutationPollEmailLoginBrowserArgs } from '../../../generated/graphql';
import { resumeEmailLinkDestination } from '../utils/emailLinkNavigation';
import { AUTHENTICATE_EMAIL_LOGIN_CODE, REQUEST_EMAIL_LOGIN_LINK, POLL_EMAIL_LOGIN_BROWSER } from '../queries';

export interface EmailLinkSignInProps {
  flow?: string;
  allowSignup?: boolean;
  returnPath?: string;
  presentation?: 'page' | 'modal' | 'embedded';
  onClose?: () => void;
}

/** Presentation is independent of the tenant's credential/profile policy. */
export function EmailLinkSignIn({ flow = 'account', allowSignup = false, returnPath = '/', presentation = 'page', onClose = () => {} }: EmailLinkSignInProps) {
  const apollo = useApolloClient();
  const [browserRequest, setBrowserRequest] = useState<{ token: string; label: string; expiresAt: number }>();
  const [waitingError, setWaitingError] = useState('');
  const [code, setCode] = useState('');
  const [codeAvailable, setCodeAvailable] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [codeResult, setCodeResult] = useState<EmailLoginLinkResult>();
  const [verifyCode, { loading: verifyingCode, error: codeError }] = useMutation<Pick<Mutation, 'authenticateEmailLoginCode'>, MutationAuthenticateEmailLoginCodeArgs>(AUTHENTICATE_EMAIL_LOGIN_CODE);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [request, { loading, error }] = useMutation<Pick<Mutation, 'requestEmailLoginLink'>, MutationRequestEmailLoginLinkArgs>(REQUEST_EMAIL_LOGIN_LINK);
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  useEffect(() => {
    let stopped = false;
    let timer: number;
    const navigate = resumeEmailLinkDestination;
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'auth_token' && event.newValue) navigate(returnPath);
    };
    window.addEventListener('storage', onStorage);
    async function poll() {
      if (stopped || !browserRequest) return;
      if (localStorage.getItem('auth_token')) { navigate(returnPath); return; }
      if (Date.now() >= browserRequest.expiresAt) { setWaitingError('This browser request has expired. Request a new link to sign in here.'); return; }
      try {
        const { data } = await apollo.mutate<Pick<Mutation, 'pollEmailLoginBrowser'>, MutationPollEmailLoginBrowserArgs>({ mutation: POLL_EMAIL_LOGIN_BROWSER, variables: { token: browserRequest.token } });
        if (stopped) return;
        if (data?.pollEmailLoginBrowser.auth?.token) {
          if (!localStorage.getItem('auth_token')) localStorage.setItem('auth_token', data.pollEmailLoginBrowser.auth.token);
          navigate(data.pollEmailLoginBrowser.returnPath || returnPath);
          return;
        }
        if (data?.pollEmailLoginBrowser.status === 'EXPIRED') { setWaitingError('This browser request is no longer available. Request a new link.'); return; }
        setWaitingError('');
      } catch { if (!stopped) setWaitingError('Unable to check sign-in yet. Retrying automatically…'); }
      if (!stopped) timer = window.setTimeout(poll, 3000);
    }
    if (browserRequest) timer = window.setTimeout(poll, 3000);
    return () => { stopped = true; window.clearTimeout(timer); window.removeEventListener('storage', onStorage); };
  }, [apollo, browserRequest, returnPath]);
  const cooldown = Math.max(0, Math.ceil((retryAt - now) / 1000));
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      const { data } = await request({ variables: { input: { email: email.trim(), flow, returnPath } } });
      if (data?.requestEmailLoginLink.accepted) {
        setSent(true);
        setCodeAvailable(!!data.requestEmailLoginLink.codeAvailable);
        setCode('');
        setWaitingError('');
        const response = data.requestEmailLoginLink;
        setBrowserRequest(response.browserToken && response.browserLabel ? { token: response.browserToken, label: response.browserLabel, expiresAt: Date.now() + 10 * 60 * 1000 } : undefined);
        if (response.browserLabel) localStorage.setItem('email_login_browser_label', response.browserLabel);
        setRetryAt(Date.now() + data.requestEmailLoginLink.retryAfterSeconds * 1000);
      }
    } catch { /* Apollo supplies the visible error. */ }
  }
  async function verifyBackupCode() {
    if (!browserRequest || code.length !== 6 || verifyingCode) return;
    try {
      const { data } = await verifyCode({ variables: { browserToken: browserRequest.token, code } });
      if (data?.authenticateEmailLoginCode) { setBrowserRequest(undefined); setCodeResult(data.authenticateEmailLoginCode); }
    } catch { /* Apollo renders the verification error. */ }
  }
  if (codeResult) return <EmailLinkCallback initialResult={codeResult} defaultPath={returnPath} />;
  const content = <VStack as="form" onSubmit={submit} spacing={6} align="stretch" w="full" maxW="430px" mx="auto">
    <Heading as="h1" size="xl" textAlign="center">{sent ? 'Check Your Email' : <>One Email,<br />One Link.</>}</Heading>
    <Text textAlign="center">{sent ? `If sign-in is available for ${email}, you’ll receive a personal link. Open it to continue.` : allowSignup ? 'Enter your email to sign in or get started. No password to remember.' : 'Enter your email to sign in. No password to remember.'}</Text>
    {sent && browserRequest && <Text textAlign="center">To sign in here after opening the email in another browser, approve browser <strong>{browserRequest.label}</strong> there. This page will continue automatically.</Text>}
    {waitingError && <Alert status="warning"><AlertIcon />{waitingError}</Alert>}
    {error && <Alert status="error"><AlertIcon />{error.message}</Alert>}
    {!sent && <FormControl isRequired>
      <FormLabel htmlFor="email-link-address">Email address</FormLabel>
      <Input id="email-link-address" type="email" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} maxLength={254} size="lg" autoFocus />
    </FormControl>}
    <Tooltip label={sent ? 'Request another email. The previous sign-in link will stop working.' : 'Request a personal sign-in link by email.'}>
      <Button type="submit" size="lg" colorScheme="green" isLoading={loading} loadingText="Requesting link…" isDisabled={cooldown > 0}>
        {sent ? cooldown ? `Resend in ${cooldown}s` : 'Resend link' : 'Send me a link'}
      </Button>
    </Tooltip>
    {sent && codeAvailable && <>
      <Tooltip label="Use the code from the same email in this requesting browser."><Button type="button" variant="link" onClick={() => setShowCode(!showCode)}>{showCode ? 'Use the email link instead' : 'Use the code instead'}</Button></Tooltip>
      {showCode && <FormControl><FormLabel htmlFor="email-backup-code">Six-digit email code</FormLabel><Input id="email-backup-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/[^0-9]/g, ''))} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void verifyBackupCode(); } }} /><Tooltip label="Verify this email code and continue through the same signup or sign-in flow."><Button mt={3} type="button" colorScheme="green" isDisabled={code.length !== 6} isLoading={verifyingCode} onClick={verifyBackupCode}>Verify and continue</Button></Tooltip></FormControl>}
      {codeError && <Alert status="error"><AlertIcon />{codeError.message}</Alert>}
    </>}
    {sent && <Tooltip label="Use a different email address; this does not change an existing account."><Button variant="link" onClick={() => { setSent(false); setEmail(''); setBrowserRequest(undefined); setWaitingError(''); }}>Change email</Button></Tooltip>}
    {sent && <Text fontSize="sm" textAlign="center" role="status">Check your spam folder too. This request does not confirm delivery.</Text>}
  </VStack>;
  if (presentation === 'embedded') return content;
  if (presentation === 'modal') return <Modal isOpen onClose={onClose} isCentered><ModalOverlay /><ModalContent mx={4}><ModalBody py={10}>{content}<Button mt={6} variant="ghost" title="Close sign-in" onClick={onClose}>Close</Button></ModalBody></ModalContent></Modal>;
  return <Box minH="100dvh" display="flex" alignItems="center" p={{ base: 6, md: 12 }}>{content}</Box>;
}
